'use client';

import Script from 'next/script';
import {useEffect, useState} from 'react';
import {getCookie} from 'cookies-next';
import {CookiePreferences} from '../cookie-banner/cookie-banner';
import {disableAnalytics} from '../../lib/cookie-utils';

interface AnalyticsProps {
  analyticsUrl?: string;
  siteId?: string;
  isProduction?: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie-consent';

export function Analytics({
  analyticsUrl = process.env.NEXT_PUBLIC_ANALYTICS_URL,
  siteId = '1',
  isProduction = process.env.NEXT_PUBLIC_APP_STAGE === 'production'
}: AnalyticsProps) {
  const [hasAnalyticsConsent, setHasAnalyticsConsent] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    const checkConsent = () => {
      const savedPreferences = getCookie(COOKIE_CONSENT_KEY);
      if (savedPreferences) {
        try {
          const preferences: CookiePreferences = JSON.parse(
            savedPreferences as string
          );
          const newConsent = preferences.analytics;

          if (hasAnalyticsConsent && !newConsent) {
            disableAnalytics();
          }

          setHasAnalyticsConsent(newConsent);
        } catch (error) {
          console.error('Failed to parse cookie consent:', error);
          setHasAnalyticsConsent(false);
        }
      } else {
        setHasAnalyticsConsent(false);
      }
    };

    checkConsent();

    const handleStorageChange = () => {
      checkConsent();
    };

    window.addEventListener('cookieConsentChanged', handleStorageChange);

    const interval = setInterval(checkConsent, 1000);

    return () => {
      window.removeEventListener('cookieConsentChanged', handleStorageChange);
      clearInterval(interval);
    };
  }, [hasAnalyticsConsent]);

  if (hasAnalyticsConsent === null) {
    return null;
  }

  if (!hasAnalyticsConsent || !analyticsUrl) {
    return null;
  }

  return (
    <Script
      src={analyticsUrl}
      data-site-id={siteId}
      defer
      {...(!isProduction && {
        'data-session-replay': 'false',
        'data-tracking-errors': 'true',
        'data-web-vitals': 'true'
      })}
    />
  );
}
